import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { AlertTriangle, Trash2, RefreshCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/** iter78 — Admin "Clear Operational Data" panel. */
const DangerZonePanel = ({ api }) => {
  const [preview, setPreview] = useState({ total_documents: 0, collections_to_wipe: [], kept_collections: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [wiping, setWiping] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const { data } = await api().get('/admin/clear-operational-data/preview');
      setPreview(data || { total_documents: 0, collections_to_wipe: [], kept_collections: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(); }, []);

  const startWipe = () => {
    setConfirmText('');
    setLastResult(null);
    setDialogOpen(true);
  };

  const submitWipe = async () => {
    if (confirmText.trim() !== 'WIPE') {
      toast.error('Type WIPE exactly to confirm.');
      return;
    }
    setWiping(true);
    try {
      const { data } = await api().post('/admin/clear-operational-data', { confirm: 'WIPE' });
      setLastResult(data);
      toast.success(`Cleared ${data?.total_removed ?? 0} document(s).`);
      setDialogOpen(false);
      await fetchPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wipe failed');
    } finally {
      setWiping(false);
    }
  };

  const totalDocs = preview?.total_documents ?? 0;

  return (
    <div className="space-y-6" data-testid="danger-zone-root">
      <Card className="border-red-300 bg-red-50/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" /> Danger Zone — Clear Operational Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-700">
            This action <strong>permanently deletes</strong> all transactions, BOMs, work orders, items, locations, vendors, labels, alerts, and other
            operational records in the current environment. It is intended for clearing leftover test data <strong>before going live</strong>.
          </p>
          <p className="text-sm text-slate-700">
            <strong>Preserved:</strong> users &amp; roles, branding &amp; app settings, item categories, BOM linked-file templates, label templates,
            serial configs, shifts, timecodes, currency, and TV stations.
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPreview}
              disabled={loading}
              data-testid="danger-refresh-preview-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh preview
            </Button>
            <Button
              variant="destructive"
              onClick={startWipe}
              disabled={loading || totalDocs === 0}
              data-testid="danger-open-wipe-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Operational Data
            </Button>
            <Badge variant="outline" className="text-red-700 border-red-300">
              {totalDocs.toLocaleString()} document{totalDocs === 1 ? '' : 's'} pending removal
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Preview — what will be removed</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : preview.collections_to_wipe.length === 0 ? (
            <p className="text-sm text-emerald-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Nothing to remove — database is already clean.
            </p>
          ) : (
            <Table data-testid="danger-preview-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Collection</TableHead>
                  <TableHead className="text-right">Documents</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.collections_to_wipe.map(r => (
                  <TableRow key={r.collection}>
                    <TableCell className="font-mono text-xs">{r.collection}</TableCell>
                    <TableCell className="text-right">{r.count?.toLocaleString?.() ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {preview.kept_collections?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Preserved collections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {preview.kept_collections.map(c => (
                <Badge key={c} variant="secondary" className="font-mono text-[11px]">{c}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {lastResult && (
        <Card className="border-emerald-300 bg-emerald-50/40">
          <CardHeader>
            <CardTitle className="text-base text-emerald-800">Last run</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-emerald-900">
              Removed <strong>{lastResult.total_removed}</strong> document(s) across{' '}
              <strong>{lastResult.wiped?.length ?? 0}</strong> collection(s).
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="danger-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Permanently clear operational data?
            </DialogTitle>
            <DialogDescription>
              About to delete <strong>{totalDocs.toLocaleString()}</strong> document(s) across{' '}
              <strong>{preview.collections_to_wipe.length}</strong> collection(s). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="danger-confirm-input">Type <span className="font-mono font-bold">WIPE</span> to confirm</Label>
            <Input
              id="danger-confirm-input"
              data-testid="danger-confirm-input"
              autoFocus
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="WIPE"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={wiping} data-testid="danger-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitWipe}
              disabled={wiping || confirmText.trim() !== 'WIPE'}
              data-testid="danger-confirm-wipe-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {wiping ? 'Wiping…' : 'Wipe now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DangerZonePanel;
